import React from 'react';
import { Formik } from 'formik';
import { Col, Row, Tooltip } from 'antd';
import { Form, FormItem, Input, ResetButton, SubmitButton, Select, Radio } from 'formik-antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { DatePicker, IntlMessages } from '@/components';
import * as Yup from 'yup';
import { useGetTemplatesQuery } from '../../../TemplateBuilder/api/templatesApiSlice';
import { PrivateComponent } from '../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../const/permissions';
import makeTemplatesQueryParams from '../../../../lib/makeTemplatesQueryParams';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import getSelectOptions from '../../../../lib/getSelectOptions';
import { TEMPLATE_CONTENT_TYPES_MAP } from '../../../TemplateBuilder/const/templates';
import { DATE_FORMAT } from '../../../../const/system';
import { errorMessages } from '../../../../const/dateValidator';

const validationSchema = Yup.lazy((values) =>
  Yup.object().shape({
    id: Yup.string(),
    title: Yup.string().max(255, 'Maximum 255 symbols required'),
    categories: Yup.array(),
    image_type: Yup.string(),
    type: Yup.string(),
    template: Yup.string(),
    released_at: Yup.date().nullable().before(values?.released_to, errorMessages.releasedAt.before),
    released_to: Yup.date().nullable().after(values?.released_at, errorMessages.releasedTo.after),
  }),
);

const FormCollectionsFilter = ({
  initialValues,
  formErrors = {},
  onSubmit,
  onCancel,
  isSubmitting,
  imageTypeOptions,
  typeOptions,
  categoriesOptions,
  isCollectionTypeDisabled,
  defaultCollectionType,
}) => {
  const intl = useIntl();

  const { data: { data: templates } = { data: [] }, isFetching: isFetchingTemplates } = useGetTemplatesQuery({
    queryParams: makeTemplatesQueryParams('is_used', TEMPLATE_CONTENT_TYPES_MAP.COLLECTION),
  });

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={formErrors}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onReset={onCancel}
    >
      {({ isValid, dirty, values }) => {
        return (
          <Form layout="vertical">
            <FormItem
              name="id"
              label={
                <Row className="w-full" justify="space-between" align="middle">
                  <IntlMessages id="ui-general-id" />
                  <Tooltip placement="topRight" title={<IntlMessages id="ui-general-filter-id-key-help" />}>
                    <InfoCircleOutlined className="hp-ml-8" />
                  </Tooltip>
                </Row>
              }
            >
              <Input name="id" placeholder={intl.formatMessage({ id: 'ui-general-ids-placeholder' })} />
            </FormItem>

            <FormItem label={intl.formatMessage({ id: 'ui-general-title' })} name="title">
              <Input name="title" placeholder={intl.formatMessage({ id: 'ui-general-specify-title' })} />
            </FormItem>

            <FormItem
              label={intl.formatMessage({ id: 'collection-form-categories-title' })}
              name="categories"
            >
              <Select
                name="categories"
                mode="multiple"
                maxTagCount="responsive"
                allowClear
                showSearch={{
                  filterOption: (input, option) => makeSelectFilterOption(option.label, input),
                }}
                options={categoriesOptions}
                loading={isSubmitting}
                placeholder={intl.formatMessage({ id: 'ui-general-choose-categories' })}
              />
            </FormItem>

            <FormItem
              label={intl.formatMessage({ id: 'collection-form-image-type-title' })}
              name="image_type"
            >
              <Select
                allowClear
                name="image_type"
                options={imageTypeOptions}
                placeholder={intl.formatMessage({ id: 'collection-form-specify-image-type' })}
              />
            </FormItem>

            <FormItem label={intl.formatMessage({ id: 'collection-form-collection-type-title' })} name="type">
              <Select
                allowClear
                showSearch
                name="type"
                value={defaultCollectionType ?? values?.type}
                disabled={isCollectionTypeDisabled}
                options={typeOptions}
                placeholder={intl.formatMessage({ id: 'collection-form-choose-collection-type' })}
              />
            </FormItem>

            <PrivateComponent allowedPermissions={[Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.FILTER]}>
              <FormItem label={intl.formatMessage({ id: 'ui-general-template' })} name="template">
                <Select
                  allowClear
                  showSearch={{
                    filterOption: (input, option) => makeSelectFilterOption(option.label, input),
                  }}
                  loading={isFetchingTemplates}
                  name="template"
                  options={getSelectOptions(templates)}
                  placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                />
              </FormItem>
            </PrivateComponent>

            <Row gutter={16}>
              <Col span={12}>
                <FormItem label={intl.formatMessage({ id: 'ui-general-released-at' })} name="released_at">
                  <DatePicker
                    name="released_at"
                    format={DATE_FORMAT}
                    showToday
                    fromFormikValue={(value) => (value ? dayjs(value) : null)}
                    toFormikValue={(dateString) => dateString}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem label={intl.formatMessage({ id: 'ui-general-released-to' })} name="released_to">
                  <DatePicker
                    name="released_to"
                    format={DATE_FORMAT}
                    fromFormikValue={(value) => (value ? dayjs(value) : null)}
                    toFormikValue={(dateString) => dateString}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[16, 16]} justify="start">
              <Col span={12}>
                <FormItem label={intl.formatMessage({ id: 'ui-general-published' })} name="is_published">
                  <Radio.Group
                    size="small"
                    name="is_published"
                    value={values.is_published ?? ''}
                    defaultValue=""
                  >
                    <Radio.Button value="">{intl.formatMessage({ id: 'ui-general-all' })}</Radio.Button>
                    <Radio.Button value={1}>{intl.formatMessage({ id: 'ui-general-yes' })}</Radio.Button>
                    <Radio.Button value={0}>{intl.formatMessage({ id: 'ui-general-no' })}</Radio.Button>
                  </Radio.Group>
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[16, 16]} justify="end">
              <Col>
                <ResetButton disabled={false}>
                  <IntlMessages id="ui-general-reset" />
                </ResetButton>
              </Col>

              <Col>
                <SubmitButton type="primary" loading={isSubmitting} disabled={!isValid || !dirty}>
                  <IntlMessages id="ui-general-apply" />
                </SubmitButton>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

FormCollectionsFilter.propTypes = {
  initialValues: PropTypes.object.isRequired,
  formErrors: PropTypes.object,
  imageTypeOptions: PropTypes.array.isRequired,
  typeOptions: PropTypes.array.isRequired,
  categoriesOptions: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isCollectionTypeDisabled: PropTypes.bool,
  defaultCollectionType: PropTypes.string,
};

export default FormCollectionsFilter;
